import { createFileRoute } from "@tanstack/react-router";
import { Courses } from "@/lib/lazyPages";
import { SEO, breadcrumbJsonLd } from "@/config/seoConfig";
import { seoHead, SITE_BASE_URL } from "@/lib/seo/head";
import { getPublicCourses, type PublicCourseLevel } from "@/lib/publicCourses.functions";

/**
 * Public, indexable course catalogue.
 *
 * /learning-journeys is the signed-in shell and redirects crawlers to the
 * noindex /auth page, so search results had no real course content to show.
 * This route server-renders the catalogue (and is prerendered at build time),
 * with ItemList + Course structured data so each course is machine-readable.
 */
function courseItemList(levels: PublicCourseLevel[]) {
  const courses = levels.flatMap((l) => l.courses);
  if (!courses.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "LeadXkids life skills courses for children aged 8-14",
    numberOfItems: courses.length,
    itemListElement: courses.map((course, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Course",
        name: course.title,
        description: course.tagline || course.description || course.learningPoints[0] || course.title,
        url: `${SITE_BASE_URL}/courses/${course.slug}`,
        ...(course.thumbnailUrl ? { image: course.thumbnailUrl } : {}),
        inLanguage: "en-IN",
        educationalLevel: "Children aged 8-14",
        teaches: course.learningPoints,
        provider: {
          "@type": "Organization",
          name: "LeadXkids",
          url: SITE_BASE_URL,
        },
      },
    })),
  };
}

export const Route = createFileRoute("/courses/")({
  loader: () => getPublicCourses(),
  head: ({ loaderData }) => {
    const seo = seoHead({
      ...SEO.courses,
      jsonLd: [
        breadcrumbJsonLd([
          { name: "Home", url: "/" },
          { name: "Courses", url: "/courses" },
        ]),
        courseItemList(loaderData ?? []),
      ],
    });
    return seo;
  },
  component: CoursesRoute,
});

function CoursesRoute() {
  const levels = Route.useLoaderData();
  return <Courses levels={levels ?? []} />;
}
